import type { FC } from 'react';
import React, { useImperativeHandle, useState } from 'react';
import styles from './mobileHeader.module.scss';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlassIcon } from '@phosphor-icons/react/dist/csr/MagnifyingGlass';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';
import { SparkleIcon } from '@phosphor-icons/react/dist/csr/Sparkle';
import IconButton from '@mui/material/IconButton';

export type MobileSearchInputProps = {
  query: string;
  onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: (e: React.MouseEvent) => void;
  onSearchClick: (e?: React.MouseEvent) => void;
  onAskAgent: () => void;
  showAskAgentAction: boolean;
  isAskAgentLoading: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  searchRef: React.RefObject<HTMLDivElement | null>;
  clearRef: React.RefObject<HTMLDivElement | null>;
  mobileToggleRef: React.RefObject<{ toggle: () => void } | null>;
};

const SearchInput: FC<MobileSearchInputProps> = ({
  query,
  onSearch,
  onClear,
  onSearchClick,
  onAskAgent,
  showAskAgentAction,
  isAskAgentLoading,
  searchRef,
  inputRef,
  clearRef,
  mobileToggleRef,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'searchBar' });
  const { mobileControl, searchIcon, closedSearch, close, askAgentIcon } = styles;

  const [isOpen, setIsOpen] = useState(false);

  useImperativeHandle(
    mobileToggleRef,
    () => ({
      toggle: () => setIsOpen(!isOpen),
    }),
    [isOpen],
  );

  const toggle = () => {
    setIsOpen(!isOpen);

    if (!isOpen) {
      setTimeout(() => {
        onSearchClick();
      }, 0);
    }
  };

  const onClearClick = (e: React.MouseEvent) => {
    if (query.length > 0) {
      onClear(e);
    } else {
      toggle();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    onAskAgent();
  };

  if (!isOpen) {
    return (
      <div onClick={toggle} className={closedSearch}>
        <MagnifyingGlassIcon size={24} />
      </div>
    );
  }

  return (
    <div className={mobileControl}>
      <div ref={searchRef} className={searchIcon} data-testid="search-icon" onClick={onSearchClick}>
        <MagnifyingGlassIcon size={16} />
        <input
          id="search-input"
          ref={inputRef}
          autoComplete="off"
          data-testid="search-input"
          placeholder={t('placeholder')}
          type="text"
          value={query}
          name="searchTerm"
          onChange={onSearch}
          onKeyDown={handleKeyDown}
          autoFocus={false}
          tabIndex={-1}
        />
        {showAskAgentAction ? (
          <IconButton
            className={askAgentIcon}
            onClick={onAskAgent}
            disabled={isAskAgentLoading}
            aria-label={t('askAgent')}
          >
            <SparkleIcon size={18} />
          </IconButton>
        ) : null}
        <div ref={clearRef} className={close} data-testid="close-icon" onClick={onClearClick}>
          <XIcon size={18} />
        </div>
      </div>
    </div>
  );
};

export default SearchInput;
